import { IncidentQuery } from '@/graphql/generated/operations'
import {
  EventType,
  IncidentActionType,
  ResolutionSegmentType,
} from '@/graphql/generated/schemas'
import { DOWN, SCROLL, UP } from '@/utils/analytics'
import { removeUnderscoreAndCapitalize } from '@/utils/formats'

import { EventsEdgeT, EventsT } from '../types/types'

export interface HighlightedEventI {
  externalId: string
  detectedAt: string
}

type IncidentActions = IncidentQuery['incident']['actions']

/**
 * Extracts event references from resolved incident actions.
 *
 * Filters incident actions to find those with type RESOLVED, then extracts
 * externalId and detectedAt from EVENT_REF segments in the resolutionContent.
 *
 * @param actions - Array of incident actions
 * @returns Array of event references with externalId and detectedAt
 */
export const getResolvedActionEventRefs = (
  actions: IncidentActions
): HighlightedEventI[] => {
  if (!actions) return []

  return actions.flatMap((action) => {
    if (action?.type !== IncidentActionType.Resolved) return []

    const segments = action.resolutionContent?.segments ?? []

    return segments
      .filter(
        (segment) =>
          segment.segmentType === ResolutionSegmentType.EventRef &&
          segment.externalId &&
          segment.detectedAt
      )
      .map((segment) => ({
        externalId: segment.externalId!,
        detectedAt: segment.detectedAt!,
      }))
  })
}

/**
 * Checks if an event is highlighted from a resolution EVENT_REF click.
 *
 * @param event - The event node to check
 * @param highlightedEvent - The currently highlighted event from resolution (if any)
 * @returns True if the event matches the highlighted event
 */
export const isHighlightedFromResolution = (
  event: Pick<EventsT, 'externalId' | 'detectedAt'>,
  highlightedEvent: HighlightedEventI | null
): boolean => {
  return (
    highlightedEvent?.externalId === event.externalId &&
    highlightedEvent?.detectedAt === event.detectedAt
  )
}

/**
 * Checks if an event matches any of the resolved action event references.
 *
 * @param event - The event node to check
 * @param resolvedActionEventRefs - Array of event refs from resolved actions
 * @returns True if the event matches any resolved action event ref
 */
export const matchesResolvedActionEventRef = (
  event: Pick<EventsT, 'externalId' | 'detectedAt'>,
  resolvedActionEventRefs: HighlightedEventI[]
): boolean => {
  return resolvedActionEventRefs.some(
    (ref) =>
      ref.externalId === event.externalId && ref.detectedAt === event.detectedAt
  )
}

/**
 * Filters events for the Incident tab display.
 *
 * When filtering is enabled, includes events that either:
 * - Match the incident's associated event IDs (e.g., tailgating events)
 * - Match any event reference from resolved actions
 *
 * @param events - Array of event edges to filter
 * @param showOnlyIncidentRelatedEvents - Whether to apply filtering
 * @param incidentEventExternalIds - Event IDs associated with the incident
 * @param resolvedActionEventRefs - Event refs from resolved actions to include
 * @returns Filtered array of events, or all events if filtering is disabled
 */
export const filterEventsForIncidentTab = (
  events: EventsEdgeT[] | undefined,
  showOnlyIncidentRelatedEvents: boolean,
  incidentEventExternalIds: string[] | undefined,
  resolvedActionEventRefs: HighlightedEventI[] = []
): EventsEdgeT[] | undefined => {
  if (!showOnlyIncidentRelatedEvents) {
    return events
  }

  return events?.filter((e) => {
    if (matchesResolvedActionEventRef(e.node, resolvedActionEventRefs)) {
      return true
    }
    return incidentEventExternalIds?.includes(e.node.externalId) ?? false
  })
}

// Mixpanel events
export const VIEW_TL_EVENT = 'View trace logs'
export const VIEW_TL_TAB_EVENT = 'tab - trace logs'
export const DEVICES_TAB_EVENT = 'Devices'
export const FACILITIES_TAB_EVENT = 'Facilities'
export const INCIDENT_TAB_EVENT = 'Incident'
export const ADDITIONAL_TL_EVENTS = 'viewing additional trace log events'
export const SCROLL_UP_EVENT = `${SCROLL} ${UP} ${ADDITIONAL_TL_EVENTS}`
export const SCROLL_DOWN_EVENT = `${SCROLL} ${DOWN} ${ADDITIONAL_TL_EVENTS}`

/**
 * Returns a formatted event name by capitalizing and removing underscores from the event type.
 * If the event type is 'Other' or null, attempts to use the ACS event type.
 * Returns 'Unknown' if neither type is available.
 *
 * @param event - The event object to extract the name from.
 * @returns The formatted event name or 'Unknown'.
 */
export const getEventName = (event: EventsT) => {
  if (event?.type !== EventType.Other && event?.type !== null) {
    return removeUnderscoreAndCapitalize(event?.type)
  }
  if (event?.acsEventType) {
    return removeUnderscoreAndCapitalize(event?.acsEventType)
  }
  return 'Unknown'
}

export const getMixpanelEventData = (incident: IncidentQuery['incident']) => ({
  device_id: incident?.devices[0]?.id,
  facility_id: incident?.facility?.id,
  incident_id: incident?.id,
})
